/**
 * Detects when a new frontend bundle has been deployed by polling
 * /version.json. The build step (scripts/write-version.js via the
 * `prebuild` npm script) stamps this file with a fresh buildId on
 * every `yarn build`.
 *
 * Fetches are bypassed-from-cache two ways (belt + suspenders):
 *   1. `cache: "no-store"` at the Fetch API level
 *   2. `?t=<ms>` cache-buster query string
 *
 * ...so platform/CDN-level Cache-Control headers on /version.json can't
 * mask a new deploy.
 */

const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 60 * 1000; // every minute — cheap, same-origin JSON

// Module-level singleton so the "initial" buildId survives React's dev-mode
// strict-mode useEffect double-invocation (and any stray component remounts).
// Without this, the second useEffect run would reset `initialBuildId` to the
// new version and the banner would never trigger.
let initialBuildId = null;
let alreadyNotified = false;

async function fetchBuildId() {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.buildId || null;
  } catch (_) {
    return null; // offline / transient — try again next tick
  }
}

/**
 * Starts polling for a new deploy.
 * @param {(newBuildId: string, oldBuildId: string) => void} onNewVersion
 *   Called once when the server's buildId differs from the one we
 *   captured at boot.
 * @returns {() => void} stop — call to cancel the interval
 */
export function startVersionPoller(onNewVersion) {
  let timer = null;

  const tick = async () => {
    const current = await fetchBuildId();
    if (!current) return;
    if (initialBuildId === null) {
      initialBuildId = current;
      return;
    }
    if (current !== initialBuildId && !alreadyNotified) {
      alreadyNotified = true;
      onNewVersion(current, initialBuildId);
    }
  };

  // Kick off immediately so we capture the initial buildId, then poll.
  tick();
  timer = setInterval(tick, POLL_INTERVAL_MS);

  return () => {
    if (timer) clearInterval(timer);
  };
}
